import CTNUpload from '@ctn/ui/components/ctn-upload';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { useGlobalContext } from '@/context/global.context';
import { useSolicitudContext } from '@/context/solicitud.context';
import { useFileDuplicate } from '@/hooks/useFileDuplicate';

import { formatDoc, getAllDocumentForm } from '@/helpers/formSolicitudes';
import { commonUpload } from '@/helpers/upload';
import type { CTNUploadRef, FileUpload } from '@ctn/ui/types/ctn-upload';

import { TIPO_DOCUMENTO } from '../../../constants/tramite.constants';
import { type RepresentanteForm } from '../config';

type Props = {
	readonly className?: string;
	readonly title: string;
};
const DocumentoPoder = ({ className, title }: Props) => {
	const classNameComponent = useMemo(() => classNames(className), [className]);
	const { getFormValues } = useSolicitudContext();
	const {
		maestrosTramite: { configuracionTramite, tipoDocumento },
	} = useGlobalContext();
	const refFile = useRef<CTNUploadRef>(null);
	const fileDuplicate = useFileDuplicate(refFile);

	const {
		formState: { errors },
		setValue,
		watch,
		trigger,
	} = useFormContext<Partial<RepresentanteForm>>();

	const documentoPoder = watch('documentoPoder');

	const onSelectFile = async (file: FileUpload) => {
		const allDocuments = getAllDocumentForm(getFormValues());
		const isDuplicate = fileDuplicate.onDuplicatedFile({ file, allDocuments });
		if (isDuplicate) return;

		const docFormatted = formatDoc({
			file,
			tipoDocumentoMaestro: tipoDocumento,
			idTipoDocumento: TIPO_DOCUMENTO.PODER_REPRESENTANTE,
		});

		setValue('documentoPoder', docFormatted, {
			shouldDirty: true,
			shouldValidate: true,
			shouldTouch: true,
		});

		await trigger('documentoPoder');
	};

	const onRemoveFile = async () => {
		setValue('documentoPoder', undefined, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});

		await trigger('documentoPoder');
	};

	return (
		<fieldset className={classNameComponent}>
			<legend className="c-pnp-heading-h4">{title}</legend>
			<CTNUpload
				{...commonUpload}
				ref={refFile}
				required
				maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
				classNameField="u-mt-16"
				label="Adjuntar documento"
				error={fileDuplicate.message ?? errors.documentoPoder?.message}
				fileLoaded={documentoPoder?.nombre ? { name: documentoPoder?.nombre, icon: 'pdfDuotone' } : undefined}
				onRemoveFile={onRemoveFile}
				onSelectFile={onSelectFile}
			/>
		</fieldset>
	);
};

export default DocumentoPoder;